import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { adminService } from '../../services/api';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import { FiUserPlus, FiSearch, FiEdit, FiTrash2, FiLock, FiCheck, FiX, FiSend, FiRefreshCw, FiCopy } from 'react-icons/fi';
import UserForm from '../../components/admin/UserForm';
import LifecycleModal from '../../components/admin/LifecycleModal';
import PlannedEmployment from '../../components/admin/PlannedEmployment';
import { useAuth } from '../../hooks/useAuth';
import {
  canManageSuperadmins,
  userMutationErrorKey,
  withSuperadminSelection,
} from '../../utils/userManagement';

// Styled components
const PageContainer = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const PageTitle = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const FiltersContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  padding: ${({ theme }) => theme.spacing[3]};
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  border-radius: ${({ theme }) => theme.borders.radius.lg};
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
`;

const FilterLabel = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  margin-right: ${({ theme }) => theme.spacing[2]};
`;

const FilterSelect = styled.select`
  padding: ${({ theme }) => theme.spacing[2]};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  background-color: white;
`;

const SearchContainer = styled.div`
  position: relative;
  flex: 1;
  max-width: 400px;
  margin-left: auto;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[8]};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}20;
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: ${({ theme }) => theme.spacing[2]};
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.textSecondary};
`;

// Table styles
const TableContainer = styled.div`
  overflow-x: auto;
  margin-top: ${({ theme }) => theme.spacing[4]};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHead = styled.thead`
  background-color: ${({ theme }) => theme.colors.backgroundLight};
`;

const TableHeadCell = styled.th`
  padding: ${({ theme }) => theme.spacing[3]};
  text-align: left;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semiBold};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  white-space: nowrap;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  &:hover {
    background-color: ${({ theme }) => theme.colors.backgroundLight};
  }
`;

const TableCell = styled.td`
  padding: ${({ theme }) => theme.spacing[3]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text};
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 9999px;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  text-transform: capitalize;
  
  ${({ status, theme }) => {
    if (status === 'superadmin') {
      return `
        background-color: ${theme.colors.secondary}15;
        color: ${theme.colors.secondary};
      `;
    } else if (status === 'admin') {
      return `
        background-color: ${theme.colors.primary}15;
        color: ${theme.colors.primary};
      `;
    } else if (status === 'employee') {
      return `
        background-color: ${theme.colors.info}15;
        color: ${theme.colors.info};
      `;
    } else if (status === 'customer') {
      return `
        background-color: ${theme.colors.success}15;
        color: ${theme.colors.success};
      `;
    } else if (status === 'support') {
      return `
        background-color: ${theme.colors.warning}15;
        color: ${theme.colors.warning};
      `;
    } else if (status === true) {
      return `
        background-color: ${theme.colors.success}15;
        color: ${theme.colors.success};
      `;
    } else if (status === false) {
      return `
        background-color: ${theme.colors.error}15;
        color: ${theme.colors.error};
      `;
    }
    return '';
  }}
`;

const BadgeGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const ProtectedActions = styled.span`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
`;

const ActionButton = styled(Button)`
  padding: 6px;
  margin-right: 4px;
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${({ theme }) => theme.spacing[4]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const PaginationInfo = styled.div`
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const PaginationButtons = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const AlertMessage = styled.div`
  padding: ${({ theme }) => theme.spacing[3]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  
  ${({ type, theme }) => {
    if (type === 'success') {
      return `
        background-color: ${theme.colors.success}15;
        color: ${theme.colors.success};
      `;
    } else if (type === 'error') {
      return `
        background-color: ${theme.colors.error}15;
        color: ${theme.colors.error};
      `;
    }
    return '';
  }}
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing[6]};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

// "Users" | "Token status" view switch (feedback #14)
const ViewToggle = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

// Token state badge: yellow when expiring (≤30 days), red when expired,
// green when ok, grey when there is no token at all.
const StateBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 9999px;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  text-transform: capitalize;

  ${({ state, theme }) => {
    if (state === 'expired') {
      return `
        background-color: ${theme.colors.error}15;
        color: ${theme.colors.error};
      `;
    } else if (state === 'expiring') {
      return `
        background-color: ${theme.colors.warning}15;
        color: ${theme.colors.warning};
      `;
    } else if (state === 'ok') {
      return `
        background-color: ${theme.colors.success}15;
        color: ${theme.colors.success};
      `;
    }
    return `
      background-color: ${theme.colors.backgroundLight};
      color: ${theme.colors.textSecondary};
    `;
  }}
`;

// Login-link actions block under the edit form (feedback #12, #13)
const LinkSection = styled.div`
  margin-top: ${({ theme }) => theme.spacing[4]};
  padding-top: ${({ theme }) => theme.spacing[4]};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const LinkSectionTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  margin: 0 0 ${({ theme }) => theme.spacing[2]};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`;

const LinkMeta = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing[3]};
`;

const CopyRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-top: ${({ theme }) => theme.spacing[2]};
`;

const CopyInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: ${({ theme }) => theme.spacing[2]};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-family: monospace;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
`;

const Users = () => {
  const { currentUser } = useAuth();
  const { t } = useTranslation();
  const mayManageSuperadmins = canManageSuperadmins(currentUser);
  const [view, setView] = useState('users'); // 'users' | 'tokens' (feedback #14)
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    role: '',
    isActive: '',
    search: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0
  });
  const [alert, setAlert] = useState(null);
  const [modalError, setModalError] = useState(null);

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [lifecycleAction, setLifecycleAction] = useState(null);
  const [resetPasswordInfo, setResetPasswordInfo] = useState(null);

  // Edit dialog: full user detail (list rows lack the customer/employee
  // sub-objects) + login-link actions (feedback #8, #12, #13)
  const [editUser, setEditUser] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [linkPending, setLinkPending] = useState(false);
  const [linkResult, setLinkResult] = useState(null);

  // Token status view (feedback #14)
  const [tokenRows, setTokenRows] = useState([]);
  const [tokenLoading, setTokenLoading] = useState(false);
  const [tokenFilters, setTokenFilters] = useState({ role: '', isActive: '' });
  const [tokenPendingId, setTokenPendingId] = useState(null);
  const [linkShareInfo, setLinkShareInfo] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, [filters, pagination.page]);

  useEffect(() => {
    if (view === 'tokens') {
      fetchTokenStatus();
    }
  }, [view, tokenFilters]);

  const fetchUsers = async () => {
    try {
      setLoading(true);

      // Only send non-empty filters; convert 'true'/'false' to boolean for isActive
      const params = {
        page: pagination.page,
        ...(filters.role && { role: filters.role }),
        ...(filters.search && { search: filters.search }),
        ...(filters.isActive !== '' && { isActive: filters.isActive === 'true' })
      };

      const response = await adminService.getUsers(params);
      
      setUsers(response.users);
      setPagination({
        page: response.page,
        totalPages: response.totalPages,
        total: response.total
      });
    } catch (error) {
      console.error('Error fetching users:', error);
      setAlert({
        type: 'error',
        message: 'Failed to load users. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchTokenStatus = async () => {
    try {
      setTokenLoading(true);
      const params = {
        ...(tokenFilters.role && { role: tokenFilters.role }),
        ...(tokenFilters.isActive !== '' && { isActive: tokenFilters.isActive === 'true' })
      };
      const data = await adminService.getTokenStatus(params);
      setTokenRows(data.users || []);
    } catch (error) {
      console.error('Error fetching token status:', error);
      setAlert({
        type: 'error',
        message: error.message || 'Failed to load token status. Please try again.'
      });
    } finally {
      setTokenLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    // Reset to first page when changing filters
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleTokenFilterChange = (e) => {
    const { name, value } = e.target;
    setTokenFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleSearchChange = (e) => {
    const { value } = e.target;
    // Debounce search input
    clearTimeout(window.searchTimeout);
    window.searchTimeout = setTimeout(() => {
      setFilters(prev => ({ ...prev, search: value }));
      setPagination(prev => ({ ...prev, page: 1 }));
    }, 300);
  };

  const handleNextPage = () => {
    if (pagination.page < pagination.totalPages) {
      setPagination(prev => ({ ...prev, page: prev.page + 1 }));
    }
  };

  const handlePrevPage = () => {
    if (pagination.page > 1) {
      setPagination(prev => ({ ...prev, page: prev.page - 1 }));
    }
  };

  const handleCreateUser = async (userData) => {
    try {
      const payload = withSuperadminSelection({
        email: userData.email,
        firstName: userData.firstName,
        lastName: userData.lastName,
        role: userData.role,
        language: userData.language || null,
        ...((userData.role === 'admin' || userData.role === 'support') && { webauthnEnabled: !!userData.webauthnEnabled }),
        ...(['admin', 'support'].includes(userData.role) && userData.password && { password: userData.password })
      }, userData, currentUser);
      const createdUser = await adminService.createUser(payload);
      setModalError(null);
      setShowAddModal(false);
      setAlert({
        type: 'success',
        message: 'User created successfully.'
      });
      if (createdUser && createdUser.temporaryPassword && ['admin', 'support'].includes(userData.role)) {
        setResetPasswordInfo({
          title: 'User Created',
          message: `A temporary password was generated for ${createdUser.firstName} ${createdUser.lastName}.`,
          temporaryPassword: createdUser.temporaryPassword
        });
      }
      fetchUsers();
    } catch (error) {
      console.error('Error creating user:', error);
      setModalError(t(userMutationErrorKey(error?.code, 'admin.users.errors.createFailed')));
    }
  };

  const handleUpdateUser = async (userData) => {
    try {
      const payload = withSuperadminSelection({
        firstName: userData.firstName,
        lastName: userData.lastName,
        role: userData.role,
        webauthnEnabled: userData.webauthnEnabled,
        language: userData.language || null,
        // Role-specific profile sections — only present when editing a
        // customer/employee user (the backend applies them role-matched).
        // Format-validated fields are sent as null when blank.
        ...(userData.customer && {
          customer: {
            ...userData.customer,
            cvr: userData.customer.cvr || null,
            invoiceEmail: userData.customer.invoiceEmail || null
          }
        }),
        ...(userData.employee && {
          employee: {
            ...userData.employee,
            startDate: userData.employee.startDate || null,
            hourlyRateEur:
              userData.employee.hourlyRateEur === '' || userData.employee.hourlyRateEur == null
                ? null
                : Number(userData.employee.hourlyRateEur)
          }
        })
      }, userData, currentUser, editUser);
      await adminService.updateUser(selectedUser.id, payload);
      setModalError(null);
      setShowEditModal(false);
      setAlert({
        type: 'success',
        message: 'User updated successfully.'
      });
      fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      setModalError(t(userMutationErrorKey(error?.code, 'admin.users.errors.updateFailed')));
    }
  };

  // Open the Edit dialog: fetch the full user (with customer/employee
  // sub-object and loginLinkExpiresAt) — the list rows do not carry these.
  const handleEditClick = async (user) => {
    setSelectedUser(user);
    setEditUser(null);
    setLinkResult(null);
    setModalError(null);
    setShowEditModal(true);
    try {
      setEditLoading(true);
      const fullUser = await adminService.getUserById(user.id);
      setEditUser(fullUser);
    } catch (error) {
      console.error('Error fetching user details:', error);
      setShowEditModal(false);
      setAlert({
        type: 'error',
        message: t(userMutationErrorKey(error?.code, 'admin.users.errors.loadFailed'))
      });
    } finally {
      setEditLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).catch(() => {});
    }
  };

  // "Send login link" in the Edit dialog (feedback #12)
  const handleEditSendLink = async () => {
    if (!selectedUser) return;
    try {
      setLinkPending(true);
      setLinkResult(null);
      const report = await adminService.sendInvitations([selectedUser.id]);
      const result = (report.results || [])[0] || {};
      if (result.delivery === 'queued') {
        // The outbox sends it when the mail limit allows (v1.42.0); the batch
        // completion notification says when it has gone.
        setLinkResult({
          type: 'success',
          message: `Login link queued for delivery to ${result.email} — you will be notified when it has been sent.`
        });
      } else if (result.emailed) {
        setLinkResult({
          type: 'success',
          message: `Login link e-mailed to ${result.email}.`
        });
      } else if (result.loginLink) {
        setLinkResult({
          type: 'link',
          message: 'The e-mail was not sent — copy the login link below and share it with the user:',
          loginLink: result.loginLink
        });
      } else {
        setLinkResult({
          type: 'error',
          message: result.placeholder
            ? 'This user has a placeholder e-mail address — set a real address first.'
            : 'The login link could not be sent.'
        });
      }
      // Sending rotates the link expiry — refresh the shown expiry date.
      try {
        const fresh = await adminService.getUserById(selectedUser.id);
        setEditUser(fresh);
      } catch (refreshError) {
        console.error('Error refreshing user details:', refreshError);
      }
    } catch (error) {
      console.error('Error sending login link:', error);
      setLinkResult({
        type: 'error',
        message: error.message || 'Failed to send the login link. Please try again.'
      });
    } finally {
      setLinkPending(false);
    }
  };

  // "Renew token (365 days)" in the Edit dialog (feedback #13)
  const handleEditRenewLink = async () => {
    if (!selectedUser) return;
    try {
      setLinkPending(true);
      setLinkResult(null);
      const result = await adminService.renewLoginLink(selectedUser.id);
      setEditUser(prev => (prev ? { ...prev, loginLinkExpiresAt: result.loginLinkExpiresAt } : prev));
      setLinkResult({
        type: 'success',
        message: `Login link renewed — valid until ${new Date(result.loginLinkExpiresAt).toLocaleDateString()}.`
      });
    } catch (error) {
      console.error('Error renewing login link:', error);
      setLinkResult({
        type: 'error',
        message: error.message || 'Failed to renew the login link. Please try again.'
      });
    } finally {
      setLinkPending(false);
    }
  };

  // Row actions in the Token status view — wired like the Edit dialog ones,
  // but they refresh the table after success (feedback #14).
  const handleTokenRenew = async (row) => {
    try {
      setTokenPendingId(row.id);
      const result = await adminService.renewLoginLink(row.id);
      setAlert({
        type: 'success',
        message: `Login link for ${row.firstName} ${row.lastName} renewed — valid until ${new Date(result.loginLinkExpiresAt).toLocaleDateString()}.`
      });
      fetchTokenStatus();
    } catch (error) {
      console.error('Error renewing login link:', error);
      setAlert({
        type: 'error',
        message: error.message || 'Failed to renew the login link. Please try again.'
      });
    } finally {
      setTokenPendingId(null);
    }
  };

  const handleTokenSendLink = async (row) => {
    try {
      setTokenPendingId(row.id);
      const report = await adminService.sendInvitations([row.id]);
      const result = (report.results || [])[0] || {};
      if (result.delivery === 'queued') {
        setAlert({
          type: 'success',
          message: `Login link queued for delivery to ${result.email} — you will be notified when it has been sent.`
        });
      } else if (result.emailed) {
        setAlert({
          type: 'success',
          message: `Login link e-mailed to ${result.email}.`
        });
      } else if (result.loginLink) {
        setLinkShareInfo({
          name: `${row.firstName} ${row.lastName}`.trim() || row.email,
          loginLink: result.loginLink
        });
      } else {
        setAlert({
          type: 'error',
          message: result.placeholder
            ? 'This user has a placeholder e-mail address — set a real address first.'
            : 'The login link could not be sent.'
        });
      }
      fetchTokenStatus();
    } catch (error) {
      console.error('Error sending login link:', error);
      setAlert({
        type: 'error',
        message: error.message || 'Failed to send the login link. Please try again.'
      });
    } finally {
      setTokenPendingId(null);
    }
  };

  const handleDeleteUser = async () => {
    try {
      await adminService.deleteUser(selectedUser.id);
      setModalError(null);
      setShowDeleteModal(false);
      setAlert({
        type: 'success',
        message: 'User deleted successfully.'
      });
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      setModalError(t(userMutationErrorKey(error?.code, 'admin.users.errors.deleteFailed')));
    }
  };

  const handleResetPassword = async () => {
    try {
      const result = await adminService.resetUserPassword(selectedUser.id);
      setModalError(null);
      setShowResetPasswordModal(false);
      setResetPasswordInfo(result);
      setAlert({
        type: 'success',
        message: 'Password reset successfully.'
      });
    } catch (error) {
      console.error('Error resetting password:', error);
      setModalError(t(userMutationErrorKey(error?.code, 'admin.users.errors.resetFailed')));
    }
  };

  const handleActivateUser = async (userId) => {
    const user = users.find((item) => item.id === userId);
    // Business accounts use the same review as their dedicated pages. Admin
    // account maintenance keeps its existing role and self-protection checks.
    if (['employee', 'customer'].includes(user?.role)) {
      setLifecycleAction({ user, action: 'activate' });
      return;
    }
    try {
      await adminService.activateUser(userId);
      setAlert({
        type: 'success',
        message: 'User activated successfully.'
      });
      fetchUsers();
    } catch (error) {
      console.error('Error activating user:', error);
      setAlert({
        type: 'error',
        message: t(userMutationErrorKey(error?.code, 'admin.users.errors.activateFailed'))
      });
    }
  };

  const handleDeactivateUser = async (userId) => {
    const user = users.find((item) => item.id === userId);
    if (['employee', 'customer'].includes(user?.role)) {
      setLifecycleAction({ user, action: 'deactivate' });
      return;
    }
    try {
      await adminService.deactivateUser(userId);
      setAlert({
        type: 'success',
        message: 'User deactivated successfully.'
      });
      fetchUsers();
    } catch (error) {
      console.error('Error deactivating user:', error);
      setAlert({
        type: 'error',
        message: t(userMutationErrorKey(error?.code, 'admin.users.errors.deactivateFailed'))
      });
    }
  };

  return (
    <PageContainer>
      {lifecycleAction && <LifecycleModal
        user={lifecycleAction.user} action={lifecycleAction.action}
        onClose={() => setLifecycleAction(null)} onSaved={() => fetchUsers()}
      />}
      <PageHeader>
        <PageTitle>User Management</PageTitle>
        {view === 'users' && (
          <HeaderActions>
            <Button onClick={() => {
              setModalError(null);
              setShowAddModal(true);
            }}>
              <FiUserPlus style={{ marginRight: '8px' }} />
              Add User
            </Button>
          </HeaderActions>
        )}
      </PageHeader>

      <ViewToggle>
        <Button
          size="sm"
          variant={view === 'users' ? 'primary' : 'outline'}
          onClick={() => setView('users')}
        >
          Users
        </Button>
        <Button
          size="sm"
          variant={view === 'tokens' ? 'primary' : 'outline'}
          onClick={() => setView('tokens')}
        >
          Token status
        </Button>
      </ViewToggle>

      {view === 'users' && (
      <Card padding="md">
        {alert && (
          <AlertMessage type={alert.type}>
            {alert.message}
          </AlertMessage>
        )}

        <FiltersContainer>
          <FilterGroup>
            <FilterLabel htmlFor="role">Role</FilterLabel>
            <FilterSelect
              id="role"
              name="role"
              value={filters.role}
              onChange={handleFilterChange}
            >
              <option value="">All Roles</option>
              <option value="admin">Admin</option>
              <option value="employee">Employee</option>
              <option value="customer">Customer</option>
              <option value="support">Support</option>
            </FilterSelect>
          </FilterGroup>
          
          <FilterGroup>
            <FilterLabel htmlFor="isActive">Status</FilterLabel>
            <FilterSelect
              id="isActive"
              name="isActive"
              value={filters.isActive}
              onChange={handleFilterChange}
            >
              <option value="">All</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </FilterSelect>
          </FilterGroup>
          
          <SearchContainer>
            <SearchIcon>
              <FiSearch />
            </SearchIcon>
            <SearchInput
              type="text"
              placeholder="Search by name or email..."
              defaultValue={filters.search}
              onChange={handleSearchChange}
            />
          </SearchContainer>
        </FiltersContainer>
        
        {loading ? (
          <EmptyState>Loading users...</EmptyState>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <tr>
                    <TableHeadCell>Name</TableHeadCell>
                    <TableHeadCell>Email</TableHeadCell>
                    <TableHeadCell>Role</TableHeadCell>
                    <TableHeadCell>Status</TableHeadCell>
                    <TableHeadCell>Created</TableHeadCell>
                    <TableHeadCell>Last Login</TableHeadCell>
                    <TableHeadCell>Actions</TableHeadCell>
                  </tr>
                </TableHead>
                <tbody>
                  {users.length > 0 ? (
                    users.map(user => (
                      <TableRow key={user.id}>
                        <TableCell>{user.firstName} {user.lastName}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          <BadgeGroup>
                            <Badge status={user.role}>{user.role}</Badge>
                            {user.role === 'admin' && user.isSuperAdmin === true && (
                              <Badge status="superadmin">{t('admin.users.superadmin.badge')}</Badge>
                            )}
                          </BadgeGroup>
                        </TableCell>
                        <TableCell>
                          <Badge status={user.isActive}>
                            {user.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                          <PlannedEmployment employee={user} />
                        </TableCell>
                        <TableCell>{new Date(user.createdAt).toLocaleDateString()}</TableCell>
                        <TableCell>
                          {user.lastLogin 
                            ? new Date(user.lastLogin).toLocaleDateString() 
                            : 'Never'}
                        </TableCell>
                        <TableCell>
                          {user.role === 'admin' && user.isSuperAdmin === true && !mayManageSuperadmins ? (
                            <ProtectedActions
                              title={t('admin.users.superadmin.protectedHint')}
                              aria-label={t('admin.users.superadmin.protectedHint')}
                            >
                              {t('admin.users.superadmin.protected')}
                            </ProtectedActions>
                          ) : (
                            <>
                              <ActionButton
                                variant="icon"
                                title="Edit"
                                onClick={() => handleEditClick(user)}
                              >
                                <FiEdit />
                              </ActionButton>

                              {/* The logged-in admin cannot deactivate or delete
                                  their own account (feedback #10) */}
                              {String(user.id) !== String(currentUser?.id) && (
                                user.isActive ? (
                                  <ActionButton
                                    variant="icon"
                                    title="Deactivate"
                                    onClick={() => handleDeactivateUser(user.id)}
                                  >
                                    <FiX />
                                  </ActionButton>
                                ) : (
                                  <ActionButton
                                    variant="icon"
                                    title="Activate"
                                    onClick={() => handleActivateUser(user.id)}
                                  >
                                    <FiCheck />
                                  </ActionButton>
                                )
                              )}

                              {mayManageSuperadmins && ['admin', 'support'].includes(user.role) && (
                                <ActionButton
                                  variant="icon"
                                  title="Reset Password"
                                  onClick={() => {
                                    setModalError(null);
                                    setSelectedUser(user);
                                    setShowResetPasswordModal(true);
                                  }}
                                >
                                  <FiLock />
                                </ActionButton>
                              )}

                              {String(user.id) !== String(currentUser?.id) && (
                                <ActionButton
                                  variant="icon"
                                  title="Delete"
                                  onClick={() => {
                                    setModalError(null);
                                    setSelectedUser(user);
                                    setShowDeleteModal(true);
                                  }}
                                >
                                  <FiTrash2 />
                                </ActionButton>
                              )}
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} style={{ textAlign: 'center' }}>
                        No users found matching your filters.
                      </TableCell>
                    </TableRow>
                  )}
                </tbody>
              </Table>
            </TableContainer>
            
            <Pagination>
              <PaginationInfo>
                Showing {Math.min((pagination.page - 1) * 10 + 1, pagination.total)} to {Math.min(pagination.page * 10, pagination.total)} of {pagination.total} users
              </PaginationInfo>
              <PaginationButtons>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handlePrevPage}
                  disabled={pagination.page === 1}
                >
                  Previous
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleNextPage}
                  disabled={pagination.page === pagination.totalPages}
                >
                  Next
                </Button>
              </PaginationButtons>
            </Pagination>
          </>
        )}
      </Card>
      )}

      {/* Token status view (feedback #14) */}
      {view === 'tokens' && (
      <Card padding="md">
        {alert && (
          <AlertMessage type={alert.type}>
            {alert.message}
          </AlertMessage>
        )}

        <FiltersContainer>
          <FilterGroup>
            <FilterLabel htmlFor="tokenIsActive">Status</FilterLabel>
            <FilterSelect
              id="tokenIsActive"
              name="isActive"
              value={tokenFilters.isActive}
              onChange={handleTokenFilterChange}
            >
              <option value="">All</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel htmlFor="tokenRole">Role</FilterLabel>
            <FilterSelect
              id="tokenRole"
              name="role"
              value={tokenFilters.role}
              onChange={handleTokenFilterChange}
            >
              <option value="">All Roles</option>
              <option value="admin">Admin</option>
              <option value="employee">Employee</option>
              <option value="customer">Customer</option>
              <option value="support">Support</option>
            </FilterSelect>
          </FilterGroup>
        </FiltersContainer>

        {tokenLoading ? (
          <EmptyState>Loading token status...</EmptyState>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <tr>
                  <TableHeadCell>Name</TableHeadCell>
                  <TableHeadCell>Email</TableHeadCell>
                  <TableHeadCell>Role</TableHeadCell>
                  <TableHeadCell>Active</TableHeadCell>
                  <TableHeadCell>Token type</TableHeadCell>
                  <TableHeadCell>Registered browsers</TableHeadCell>
                  <TableHeadCell>Expires</TableHeadCell>
                  <TableHeadCell>State</TableHeadCell>
                  <TableHeadCell>Actions</TableHeadCell>
                </tr>
              </TableHead>
              <tbody>
                {tokenRows.length > 0 ? (
                  tokenRows.map(row => (
                    <TableRow key={row.id}>
                      <TableCell>{row.firstName} {row.lastName}</TableCell>
                      <TableCell>{row.email}</TableCell>
                      <TableCell>
                        <Badge status={row.role}>{row.role}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge status={row.isActive}>
                          {row.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>{row.tokenSource === 'link' ? 'Link' : 'Session'}</TableCell>
                      <TableCell>{row.linkDevices == null ? '—' : `${row.linkDevices} / 2`}</TableCell>
                      <TableCell>
                        {row.tokenExpiresAt
                          ? new Date(row.tokenExpiresAt).toLocaleDateString()
                          : '—'}
                      </TableCell>
                      <TableCell>
                        <StateBadge state={row.tokenState}>{row.tokenState}</StateBadge>
                      </TableCell>
                      <TableCell>
                        {(row.role === 'employee' || row.role === 'customer') && (
                          <div style={{ display: 'flex', gap: '4px' }}>
                            <Button
                              size="sm"
                              variant="outline"
                              title="Extend the login link by 365 days from today"
                              onClick={() => handleTokenRenew(row)}
                              disabled={tokenPendingId !== null}
                            >
                              <FiRefreshCw />
                              Renew
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              title="Send (or resend) the login link by e-mail"
                              onClick={() => handleTokenSendLink(row)}
                              disabled={tokenPendingId !== null || row.isActive === false}
                            >
                              <FiSend />
                              Send link
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} style={{ textAlign: 'center' }}>
                      No users found matching your filters.
                    </TableCell>
                  </TableRow>
                )}
              </tbody>
            </Table>
          </TableContainer>
        )}
      </Card>
      )}

      {/* Add User Modal */}
      {showAddModal && (
        <Modal
          title="Add New User"
          onClose={() => {
            setModalError(null);
            setShowAddModal(false);
          }}
        >
          {modalError && <AlertMessage type="error" role="alert">{modalError}</AlertMessage>}
          <UserForm 
            onSubmit={handleCreateUser}
            onCancel={() => {
              setModalError(null);
              setShowAddModal(false);
            }}
            canManageSuperadmin={mayManageSuperadmins}
          />
        </Modal>
      )}
      
      {/* Edit User Modal — works on the full user detail (with the
          customer/employee sub-object and loginLinkExpiresAt) */}
      {showEditModal && selectedUser && (
        <Modal
          title="Edit User"
          onClose={() => {
            setModalError(null);
            setShowEditModal(false);
          }}
        >
          {modalError && <AlertMessage type="error" role="alert">{modalError}</AlertMessage>}
          {editLoading || !editUser ? (
            <EmptyState>Loading user details...</EmptyState>
          ) : (
            <>
              <UserForm
                user={editUser}
                onSubmit={handleUpdateUser}
                onCancel={() => {
                  setModalError(null);
                  setShowEditModal(false);
                }}
                isEditing
                canManageSuperadmin={mayManageSuperadmins}
              />

              {/* Login link actions — employee/customer users only (feedback #12, #13) */}
              {(editUser.role === 'employee' || editUser.role === 'customer') && (
                <LinkSection>
                  <LinkSectionTitle>Login link</LinkSectionTitle>
                  <LinkMeta>
                    {editUser.loginLinkExpiresAt
                      ? `Link valid until ${new Date(editUser.loginLinkExpiresAt).toLocaleDateString()}`
                      : 'No login link has been issued yet — send one below.'}
                  </LinkMeta>
                  <LinkMeta>
                    Resending preserves this user's working link and session. Only active accounts with a real email address can be invited.
                    {' '}Customers and employees can also request access from the Tempus homepage.
                  </LinkMeta>

                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleEditSendLink}
                      disabled={linkPending || editUser.isActive === false}
                    >
                      <FiSend />
                      {linkPending ? 'Working…' : 'Send / resend login link'}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleEditRenewLink}
                      disabled={linkPending}
                    >
                      <FiRefreshCw />
                      {linkPending ? 'Working…' : 'Extend link (365 days)'}
                    </Button>
                  </div>

                  {linkResult && linkResult.type !== 'link' && (
                    <AlertMessage type={linkResult.type} style={{ marginTop: '12px', marginBottom: 0 }}>
                      {linkResult.message}
                    </AlertMessage>
                  )}

                  {linkResult && linkResult.type === 'link' && (
                    <div style={{ marginTop: '12px' }}>
                      <LinkMeta style={{ marginBottom: 0 }}>{linkResult.message}</LinkMeta>
                      <CopyRow>
                        <CopyInput
                          readOnly
                          value={linkResult.loginLink}
                          onFocus={(e) => e.target.select()}
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          title="Copy the login link"
                          onClick={() => copyToClipboard(linkResult.loginLink)}
                        >
                          <FiCopy />
                          Copy
                        </Button>
                      </CopyRow>
                    </div>
                  )}
                </LinkSection>
              )}
            </>
          )}
        </Modal>
      )}
      
      {/* Delete User Modal */}
      {showDeleteModal && selectedUser && (
        <Modal
          title="Delete User"
          onClose={() => {
            setModalError(null);
            setShowDeleteModal(false);
          }}
        >
          {modalError && <AlertMessage type="error" role="alert">{modalError}</AlertMessage>}
          <p>Are you sure you want to delete the user {selectedUser.firstName} {selectedUser.lastName}?</p>
          <p>This action cannot be undone.</p>
          
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '24px' }}>
            <Button 
              variant="outline" 
              onClick={() => {
                setModalError(null);
                setShowDeleteModal(false);
              }}
            >
              Cancel
            </Button>
            <Button 
              variant="danger" 
              onClick={handleDeleteUser}
            >
              Delete
            </Button>
          </div>
        </Modal>
      )}
      
      {/* Reset Password Modal */}
      {showResetPasswordModal && selectedUser && (
        <Modal
          title="Reset Password"
          onClose={() => {
            setModalError(null);
            setShowResetPasswordModal(false);
          }}
        >
          {modalError && <AlertMessage type="error" role="alert">{modalError}</AlertMessage>}
          <p>Are you sure you want to reset the password for {selectedUser.firstName} {selectedUser.lastName}?</p>
          <p>A temporary password will be generated and shown to you.</p>
          
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '24px' }}>
            <Button 
              variant="outline" 
              onClick={() => {
                setModalError(null);
                setShowResetPasswordModal(false);
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleResetPassword}
            >
              Reset Password
            </Button>
          </div>
        </Modal>
      )}
      
      {/* Temporary Password Info Modal */}
      {resetPasswordInfo && (
        <Modal
          title={resetPasswordInfo.title || 'Password Reset Successful'}
          onClose={() => setResetPasswordInfo(null)}
        >
          <p>{resetPasswordInfo.message}</p>
          
          {resetPasswordInfo.temporaryPassword && (
            <>
              <p>Temporary Password:</p>
              <div style={{ 
                padding: '12px', 
                background: '#f5f5f5', 
                borderRadius: '4px',
                fontFamily: 'monospace',
                marginTop: '8px',
                marginBottom: '16px'
              }}>
                {resetPasswordInfo.temporaryPassword}
              </div>
              <p>Please securely share this with the user.</p>
            </>
          )}
          
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
            <Button onClick={() => setResetPasswordInfo(null)}>
              Close
            </Button>
          </div>
        </Modal>
      )}

      {/* Login link relay modal — shown when the token-view "Send link" could
          not e-mail the link, so the admin copies and shares it manually */}
      {linkShareInfo && (
        <Modal
          title="Login link"
          onClose={() => setLinkShareInfo(null)}
        >
          <p>
            The e-mail was not sent — copy the login link below and share it with {linkShareInfo.name}:
          </p>
          <CopyRow>
            <CopyInput
              readOnly
              value={linkShareInfo.loginLink}
              onFocus={(e) => e.target.select()}
            />
            <Button
              size="sm"
              variant="outline"
              title="Copy the login link"
              onClick={() => copyToClipboard(linkShareInfo.loginLink)}
            >
              <FiCopy />
              Copy
            </Button>
          </CopyRow>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
            <Button onClick={() => setLinkShareInfo(null)}>
              Close
            </Button>
          </div>
        </Modal>
      )}
    </PageContainer>
  );
};

export default Users;
